import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Target, TrendingUp, TrendingDown, Upload, CheckCircle2 } from "lucide-react";
import { SeminuevosMixBar } from "./SeminuevosMixBar";
import { DealersLujoBlock } from "./DealersLujoBlock";
import { ClosingRows, PROJ_COLORS } from "./ProjectionClosing";
import { ProjectionWeekTrend } from "./ProjectionWeekTrend";

const NA_KEYS = [["campaign", "Todas", "campaña"], ["channel", "Todos", "canal"], ["executive", "Todos", "ejecutivo"], ["model", "Todos", "modelo"]];

/* Tarjeta "Proyección Mensual" — se renderiza al final de la fila KPI de
 * Sales Performance. Muestra el total proyectado del mes vigente vs
 * objetivo, con delta vs semana anterior y tooltip top-5 (sucursal o
 * marca). Lee de `/api/projections/current` respetando los filtros
 * activos del dashboard.
 */
export default function ProjectionKpiCard({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tooltipBy, setTooltipBy] = useState("sucursales"); // sucursales | marcas

  const load = () => {
    setLoading(true); setErr(null);
    api.get("/projections/current", { params: {
        year: filters.year, month: filters.month,
        brand: filters.brand, showroom: filters.showroom,
        business_unit: filters.business_unit,
    }})
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "No se pudo cargar la proyección"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters.year, filters.month, filters.brand, filters.showroom, filters.business_unit]);

  // Si no hay data (admin aún no subió el Excel) mostramos un state
  // vacío invitando a cargarla.
  if (loading) {
    return (
      <div className="surface p-5 flex flex-col gap-3" data-testid="projection-card-loading">
        <div className="text-[13px] font-extrabold uppercase tracking-wider text-gray-400">Proyección Mensual</div>
        <div className="h-6 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }
  if (err || !data || data.rows === 0) {
    return (
      <div className="surface p-5" data-testid="projection-card-empty">
        <div className="flex items-center gap-2 mb-2">
          <Target size={14} style={{ color: "#64748b" }}/>
          <div className="text-[13px] font-extrabold uppercase tracking-wider text-gray-400">Proyección Mensual</div>
        </div>
        <div className="text-[17px] font-extrabold text-gray-500">—</div>
        <div className="text-[15px] text-gray-400 mt-1 font-semibold">
          {err ? err : "Admin debe cargar el Excel de proyecciones."}
        </div>
      </div>
    );
  }

  const t = data.totals || {};
  const delta = t.delta_vs_prev_week ?? 0;
  const deltaColor = delta >= 0 ? "#059669" : "#dc2626";
  const deltaIcon = delta >= 0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>;
  const pctTarget = t.pct_of_target;
  const pctColor = pctTarget == null ? "#64748b"
    : pctTarget >= 95 ? "#059669"
    : pctTarget >= 70 ? "#d97706" : "#dc2626";

  const topList = tooltipBy === "sucursales"
    ? (data.top_sucursales || [])
    : (data.top_marcas || []);

  return (
    <div className="surface p-5 relative overflow-visible" data-testid="projection-card"
         style={{ borderLeft: `3px solid ${pctColor}` }}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Target size={14} style={{ color: pctColor }}/>
          <div className="text-[13px] font-extrabold uppercase tracking-wider text-gray-500">
            Proyección Mensual
          </div>
        </div>
        <div className="pill text-[12px] font-extrabold" style={{ background: "#f1f5f9", color: "#334155" }}>
          W{data.current_week || "?"} · {data.month} {data.year}
        </div>
      </div>

      {NA_KEYS.some(([k, d]) => filters[k] && filters[k] !== d) && (
        <div className="mb-2 px-2 py-1 rounded-md text-[11px] font-bold" data-testid="projection-na-notice"
             style={{ background: "#fef3c7", color: "#92400e" }}>
          No aplica el filtro de {NA_KEYS.filter(([k, d]) => filters[k] && filters[k] !== d).map(([, , l]) => l).join(", ")}:
          la proyección viene por sucursal y marca.
        </div>
      )}

      <div className="font-display font-black text-[36px]" style={{ color: PROJ_COLORS.projection }} data-testid="projection-value">
        {Number(t.projection || 0).toLocaleString("es-DO")}
      </div>
      <div className="text-[14px] text-gray-500 font-semibold mt-1">
        <span style={{ color: PROJ_COLORS.target }}>Objetivo <strong data-testid="projection-target">{Number(t.target || 0).toLocaleString("es-DO")}</strong></span>
        {pctTarget != null && (
          <span className="ml-1" style={{ color: pctColor, fontWeight: 800 }}>
            ({pctTarget}%)
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className="pill flex items-center gap-1 text-[13px] whitespace-nowrap shrink-0"
              style={{ display: "inline-flex", height: "auto", padding: "4px 8px", background: delta >= 0 ? "#dcfce7" : "#fee2e2", color: deltaColor }}>
          {deltaIcon} ΔW = {delta >= 0 ? "+" : ""}{delta}
        </span>
        <span className="text-[13px] text-gray-400 font-semibold">
          vs semana anterior {Number(t.projection_prev_week || 0).toLocaleString("es-DO")}
        </span>
      </div>

      {t.real > 0 && (
        <div className="flex items-center gap-1 mt-2 text-[13px] font-semibold" style={{ color: PROJ_COLORS.real }} data-testid="projection-real">
          <CheckCircle2 size={10} style={{ color: PROJ_COLORS.real }}/>
          Real al corte: <strong>{Number(t.real).toLocaleString("es-DO")}</strong>
          {t.real_pct_of_target != null && (
            <span className="ml-1 text-gray-400">({t.real_pct_of_target}%)</span>
          )}
        </div>
      )}

      {data.weekly && (
        <ProjectionWeekTrend weekly={data.weekly} currentWeek={data.current_week}
                             target={t.target} color={PROJ_COLORS.projection} targetColor={PROJ_COLORS.target}/>
      )}

      {/* Mini breakdown · tabs sucursal/marca */}
      {topList.length > 0 && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px dashed #e2e8f0" }}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[12px] font-extrabold uppercase tracking-wider text-gray-400">{data.closing ? "Detalle por" : "Top 5 por"}</span>
            {["sucursales", "marcas"].map((k) => (
              <button key={k}
                      onClick={() => setTooltipBy(k)}
                      className="text-[12px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors"
                      style={{
                        background: tooltipBy === k ? "#111827" : "transparent",
                        color: tooltipBy === k ? "#fff" : "#64748b",
                        border: tooltipBy === k ? "1px solid #111827" : "1px solid #cbd5e1",
                      }}
                      data-testid={`projection-tab-${k}`}>
                {k}
              </button>
            ))}
          </div>
          {data.closing ? <ClosingRows closing={{ ...data.closing, rows: tooltipBy === "sucursales" ? data.closing.top_sucursales || [] : data.closing.top_marcas || [] }}/> : (
          <div className="space-y-1">
            {topList.map((row) => {
              const d = row.delta_vs_prev ?? 0;
              const dColor = d >= 0 ? "#059669" : "#dc2626";
              return (
                <div key={row.name}>
                <div className="flex items-center justify-between text-[14px] font-semibold">
                  <span className="truncate text-gray-600" style={{ maxWidth: 150 }}>{row.name}</span>
                  <span className="flex items-center gap-1.5">
                    <span style={{ color: PROJ_COLORS.projection, fontWeight: 800 }}>{Number(row.projection || 0).toLocaleString("es-DO")}</span>
                    <span style={{ color: PROJ_COLORS.target, fontSize: 12, fontWeight: 700 }}
                          data-testid={`projection-row-target-${row.name}`}>
                      / {Number(row.target || 0).toLocaleString("es-DO")}
                    </span>
                    <span style={{ color: dColor, fontSize: 12, fontWeight: 800 }}>
                      {d >= 0 ? "+" : ""}{d}
                    </span>
                  </span>
                </div>
                {tooltipBy === "marcas" && row.name === "Seminuevos" && data.seminuevos_mix && (
                  <SeminuevosMixBar mix={data.seminuevos_mix}/>
                )}
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}
      {data.dealers_lujo && <DealersLujoBlock dealers={data.dealers_lujo} week={data.current_week}/>}
    </div>
  );
}


/* --- Admin · upload Excel card (va en /admin/integrations) --- */
export function ProjectionsUploadCard() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const refresh = () => api.get("/projections/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setUploading(true); setMsg(null); setErr(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/projections/upload?year=2026", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(`Cargadas ${r.data.rows_written} filas · Semana ${r.data.current_week} · Meses: ${r.data.months_covered.join(", ")}`);
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="surface p-5 mb-5" data-testid="projections-upload-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ width: 36, height: 36, background: "#fef3c7", color: "#92400e" }}>
          <Target size={18}/>
        </div>
        <div className="flex-1">
          <h3 className="text-[15px] font-extrabold font-display">Proyecciones Semanales de Ventas</h3>
          <p className="text-[12px] text-gray-500 font-semibold mt-0.5">
            Carga el Excel &quot;Resultados Comercial Ventas Directas 2026&quot; cada semana.
            El sistema extrae W1..W5 por sucursal × marca × mes y actualiza la tarjeta en Sales Performance.
          </p>
        </div>
      </div>

      <form onSubmit={onUpload} className="flex gap-2 items-center flex-wrap">
        <input type="file" ref={fileRef} accept=".xlsx,.xlsm"
               className="text-[13px] font-semibold"
               data-testid="projections-file-input"/>
        <button type="submit" className="btn primary flex items-center gap-2" disabled={uploading}
                data-testid="projections-upload-btn">
          <Upload size={14}/> {uploading ? "Subiendo..." : "Subir Excel"}
        </button>
      </form>

      {msg && (
        <div className="rounded-lg p-2 mt-3 text-[12px] font-semibold flex items-center gap-2"
             style={{ background: "#dcfce7", color: "#166534" }}>
          <CheckCircle2 size={12}/> {msg}
        </div>
      )}
      {err && (
        <div className="rounded-lg p-2 mt-3 text-[12px] font-semibold"
             style={{ background: "#fee2e2", color: "#991b1b" }}>
          {err}
        </div>
      )}

      {status && status.total_rows > 0 && (
        <div className="mt-4 p-3 rounded-lg text-[12px] font-semibold"
             style={{ background: "#f1f5f9", color: "#334155" }}
             data-testid="projections-status">
          Última carga: <strong>{status.last_upload ? new Date(status.last_upload).toLocaleString("es-DO") : "—"}</strong>
          {" · "}por {status.last_upload_by || "—"}
          {" · "}Semana <strong>W{status.current_week || "?"}</strong>
          {" · "}Meses: {status.months_covered?.join(", ") || "—"}
          {" · "}<strong>{status.total_rows}</strong> filas
        </div>
      )}
    </div>
  );
}
